import type { AuthorizedResourceAccessMap } from '@/permissions/adapter';
import {
  MEETING_ROOM_PERMISSION_ACTION,
  MEETING_ROOM_PERMISSION_RESOURCE,
  PERMISSION_ACTION_CODES,
  PERMISSION_RESOURCE_CODES,
} from '@/permissions/definitions';

const buildAdminAccessMap = (
  authorizedResourceAccess: AuthorizedResourceAccessMap = {},
): AuthorizedResourceAccessMap => {
  const adminAccessMap: AuthorizedResourceAccessMap = {
    ...authorizedResourceAccess,
  };

  Object.values(PERMISSION_RESOURCE_CODES).forEach((resourceCode) => {
    adminAccessMap[resourceCode] = true;
  });

  Object.entries(PERMISSION_ACTION_CODES).forEach(
    ([resourceKey, resourceActions]) => {
      const resourceCode =
        PERMISSION_RESOURCE_CODES[
          resourceKey as keyof typeof PERMISSION_RESOURCE_CODES
        ];

      Object.values(resourceActions).forEach((actionCode) => {
        adminAccessMap[`${resourceCode}:${actionCode}`] = true;
      });
    },
  );

  return adminAccessMap;
};

/**
 * 满足 Batch C 约束：BFF 只保留登录校验，不因细粒度权限门控导致普通登录用户无法使用物资功能
 */
const buildSuppliesAccessMap = (): AuthorizedResourceAccessMap => {
  const map: AuthorizedResourceAccessMap = {};
  Object.values(PERMISSION_RESOURCE_CODES).forEach((resourceCode) => {
    map[resourceCode] = true;
  });
  Object.entries(PERMISSION_ACTION_CODES).forEach(([resourceKey, resourceActions]) => {
    const resourceCode =
      PERMISSION_RESOURCE_CODES[
        resourceKey as keyof typeof PERMISSION_RESOURCE_CODES
      ];
    Object.values(resourceActions).forEach((actionCode) => {
      map[`${resourceCode}:${actionCode}`] = true;
    });
  });

  // 会议室业务权限全量开通，对齐内部管理系统规范
  Object.values(MEETING_ROOM_PERMISSION_RESOURCE).forEach((res) => {
    map[res] = true;
    Object.values(MEETING_ROOM_PERMISSION_ACTION).forEach((act) => {
      map[`${res}:${act}`] = true;
    });
  });

  return map;
};

/**
 * @see https://umijs.org/docs/max/access#access
 * */
export default function access(
  initialState:
    | {
        authorizedResourceAccess?: AuthorizedResourceAccessMap;
        currentTenantIsAdmin?: boolean;
      }
    | undefined,
) {
  const { authorizedResourceAccess, currentTenantIsAdmin } = initialState ?? {};
  const suppliesAccess = buildSuppliesAccessMap();

  if (currentTenantIsAdmin) {
    return {
      ...buildAdminAccessMap(authorizedResourceAccess),
      ...suppliesAccess,
      tenantAdmin: true,
      modelSettings: true,
    };
  }

  return {
    ...authorizedResourceAccess,
    ...suppliesAccess,
    tenantAdmin: false,
    modelSettings: false,
  };
}
